/**
 * ILF wishlist storefront behaviour.
 * Drives all three controls — the collection card heart (ilf-wishlist-heart),
 * the product page save button (ilf-wishlist-save) and the header/drawer link
 * (data-ilf-wishlist-link). The first two are both [data-ilf-wishlist-heart].
 *
 * Calls the wishlist app's storefront API directly via App Proxy
 * (/apps/wishlist/*) — see wishlist-api-reference.md for the full contract.
 *
 * Scoped on purpose: always uses the customer's default (first) list, with no
 * multi-list picker. Multi-list was cut from v1 by the manager, so this is the
 * intended behaviour, not a placeholder.
 */
(function () {
  "use strict";

  var PROXY_PATH = "/apps/wishlist";
  var wishlistIdPromise = null;

  // What's already in the customer's list, as { productId, variantId, itemId }.
  // Filled in by hydrateSavedState(), then kept in step as they add/remove.
  var savedItems = [];

  function api(path, options) {
    return fetch(PROXY_PATH + path, {
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      ...options,
    }).then(function (res) {
      if (res.status === 204) return null;
      return res.json().then(function (body) {
        if (!res.ok) {
          var err = new Error((body && body.error) || "REQUEST_FAILED");
          err.status = res.status;
          err.code = body && body.error;
          throw err;
        }
        return body;
      });
    });
  }

  /**
   * The default list id, fetched once per page and shared by every button.
   *
   * Resolves to null when the customer has never saved anything: GET
   * /wishlists is read-only, so a shopper who is only browsing never gets a
   * list created for them. Saves send "default" instead of an id, and the
   * server creates the list at that point (and tells us its id).
   */
  function wishlistId() {
    if (!wishlistIdPromise) {
      wishlistIdPromise = api("/wishlists").then(function (data) {
        var first = data.wishlists && data.wishlists[0];
        return first ? first.id : null;
      });
      // Don't cache a failure — let the next click retry.
      wishlistIdPromise.catch(function () {
        wishlistIdPromise = null;
      });
    }
    return wishlistIdPromise;
  }

  function showToast(message) {
    var toast = document.createElement("div");
    toast.className = "ilf-wishlist-toast";
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(function () {
      toast.remove();
    }, 2500);
  }

  function markSaved(button, itemId) {
    button.classList.add("is-active");
    button.setAttribute("aria-pressed", "true");
    if (itemId) button.dataset.wishlistItemId = itemId;
  }

  function markUnsaved(button) {
    button.classList.remove("is-active");
    button.setAttribute("aria-pressed", "false");
    delete button.dataset.wishlistItemId;
  }

  /**
   * The saved entry this control currently stands for, if any.
   *
   * A product page control is variant-aware: it only counts as saved when the
   * variant on screen is the saved one, so switching to a variant they haven't
   * saved gives them an empty heart to click (and clicking it adds THAT
   * variant, instead of removing the other one). A card heart has no variant
   * picker, so any saved variant of the product counts.
   */
  function findSavedItem(button) {
    var productId = button.dataset.productId;
    var variant = String(variantId(button) || "");
    var productMatch = null;

    for (var i = 0; i < savedItems.length; i++) {
      var item = savedItems[i];
      if (item.productId !== productId) continue;
      if (variant && item.variantId === variant) return item;
      if (!productMatch) productMatch = item;
    }

    return button.hasAttribute("data-live-variant") ? null : productMatch;
  }

  function refreshButton(button) {
    var item = findSavedItem(button);
    if (item) markSaved(button, item.itemId);
    else markUnsaved(button);
  }

  /**
   * Redraw every control for this product, not just the one clicked. The
   * product page carries one save button per gallery slide (each sits inside
   * its own image, like the zoom icon), and the same product can also appear
   * as a card further down the page — they all have to agree.
   */
  function refreshProduct(button) {
    document
      .querySelectorAll(
        '[data-ilf-wishlist-heart][data-product-id="' + button.dataset.productId + '"]'
      )
      .forEach(refreshButton);
  }

  function rememberItem(button, itemId) {
    savedItems.push({
      productId: button.dataset.productId,
      variantId: String(variantId(button) || ""),
      itemId: itemId,
    });
  }

  function forgetItem(itemId) {
    savedItems = savedItems.filter(function (item) {
      return item.itemId !== itemId;
    });
  }

  /**
   * Which variant a click should save.
   *
   * On a product page the shopper can change variant after load, so the
   * server-rendered data-variant-id goes stale immediately — that is what
   * used to put the wrong (first) variant in the wishlist, and then into the
   * cart when they added it from the account page.
   *
   * The buy form's hidden input is the fix: every product section here builds
   * its form as #product-form-{section.id}, and the theme's variant picker
   * writes the selected variant into that input. It is literally the value
   * Add to cart posts, so saving it keeps the two in step. ?variant= on the
   * URL is the backup (the picker updates that too, but skips it inside
   * quick-add modals), and the rendered attribute is the last resort.
   *
   * Card hearts carry neither flag and keep the variant they were rendered
   * with, which for a card is the only variant it knows about.
   */
  function variantId(button) {
    var formId = button.dataset.productForm;
    if (formId) {
      var input = document.querySelector(
        "#" + formId + ' input[name="id"]'
      );
      if (input && input.value) return input.value;
    }

    if (button.hasAttribute("data-live-variant")) {
      var fromUrl = new URLSearchParams(window.location.search).get("variant");
      if (fromUrl) return fromUrl;
    }

    return button.dataset.variantId || undefined;
  }

  function addToWishlist(button) {
    button.classList.add("is-loading");
    var payload = {
      productId: button.dataset.productId,
      variantId: variantId(button),
      title: button.dataset.productTitle,
      vendor: button.dataset.productVendor,
      tags: button.dataset.productTags,
      imageUrl: button.dataset.productImage,
      price: button.dataset.productPrice,
    };

    wishlistId()
      .then(function (id) {
        return api("/wishlists/" + (id || "default") + "/items", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      })
      .then(function (data) {
        var itemId = data && data.item && data.item.id;
        // First ever save: the server just created the list, so hold on to its
        // id for the rest of this page (removes and later adds need it).
        if (data && data.item && data.item.wishlistId) {
          wishlistIdPromise = Promise.resolve(data.item.wishlistId);
        }
        rememberItem(button, itemId);
        refreshProduct(button);
        showToast("Added to wishlist");
      })
      .catch(function (err) {
        if (err.code === "DUPLICATE_ITEM") {
          // Already there, we just don't know its item id — treat it as saved.
          rememberItem(button, undefined);
          refreshProduct(button);
          showToast("Already in your wishlist");
        } else if (err.status === 401) {
          window.location.href = "/account/login";
        } else {
          showToast("Something went wrong, please try again");
        }
      })
      .finally(function () {
        button.classList.remove("is-loading");
      });
  }

  function removeFromWishlist(button) {
    var itemId = button.dataset.wishlistItemId;
    if (!itemId) {
      showToast("Already in your wishlist");
      return;
    }

    button.classList.add("is-loading");
    wishlistId()
      .then(function (id) {
        return api("/wishlists/" + id + "/items/" + itemId, {
          method: "DELETE",
        });
      })
      .then(function () {
        forgetItem(itemId);
        refreshProduct(button);
        showToast("Removed from wishlist");
      })
      .catch(function (err) {
        if (err.status === 401) {
          window.location.href = "/account/login";
        } else {
          showToast("Something went wrong, please try again");
        }
      })
      .finally(function () {
        button.classList.remove("is-loading");
      });
  }

  /**
   * Light up anything already saved, so a saved product still reads as saved
   * after a reload. Matches on product id only (not variant): if any variant
   * of a product is in the list, the control shows as saved.
   *
   * Reads only the first page of items (the API returns 50 per page), so a
   * customer with a very long list may see some controls start empty. Failing
   * this is harmless — saved state is a nicety, never a blocker.
   */
  function hydrateSavedState() {
    var buttons = document.querySelectorAll(
      '[data-ilf-wishlist-heart][data-logged-in="true"]'
    );
    if (!buttons.length) return;

    wishlistId()
      .then(function (id) {
        // Nothing saved yet — every control on the page stays empty.
        if (!id) return { items: [] };
        return api("/wishlists/" + id + "/items?page=1");
      })
      .then(function (data) {
        savedItems = (data.items || []).map(function (item) {
          return {
            // Both ids come back in GID form: gid://shopify/Product/123
            productId: String(item.shopifyProductId).split("/").pop(),
            variantId: String(item.shopifyVariantId || "").split("/").pop(),
            itemId: item.id,
          };
        });

        buttons.forEach(refreshButton);
      })
      .catch(function () {});
  }

  /**
   * The theme's variant picker writes the chosen variant into the buy form's
   * hidden input and fires a bubbling change — on every product section here,
   * including the ones that re-render their info column from the section
   * rendering API (the input is a new node then, but the event still bubbles).
   * That is our cue to re-check whether the variant now on screen is one they
   * have already saved.
   */
  document.addEventListener("change", function (e) {
    if (!e.target.matches('input[name="id"]')) return;

    document
      .querySelectorAll("[data-ilf-wishlist-heart][data-live-variant]")
      .forEach(refreshButton);
  });

  // Header / drawer wishlist link. Its href already points at the login page
  // for a logged-out visitor, so this handler only exists to say WHY they are
  // about to land there — with JS off the plain link still does the right
  // thing, just silently.
  document.addEventListener("click", function (e) {
    var link = e.target.closest("[data-ilf-wishlist-link]");
    if (!link) return;
    if (link.dataset.loggedIn === "true") return;

    e.preventDefault();
    showToast("Log in to see your wishlist");
    setTimeout(function () {
      window.location.href = link.getAttribute("href");
    }, 900);
  });

  document.addEventListener("click", function (e) {
    var button = e.target.closest("[data-ilf-wishlist-heart]");
    if (!button) return;
    e.preventDefault();

    if (button.dataset.loggedIn !== "true") {
      window.location.href = "/account/login";
      return;
    }

    if (button.classList.contains("is-active")) {
      removeFromWishlist(button);
    } else {
      addToWishlist(button);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hydrateSavedState);
  } else {
    hydrateSavedState();
  }
})();
